import React from 'react';

import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { Image, Description, Title, Subtitle, listItemBaseStyles } from 'components/ui/list-item-label'
import { Container, Content, Wrap, ItemIndex, DragHandle } from 'components/ui/draggable'

import { Stop, StopPointOfInterest } from 'generated/mos/org/getty/audio'

import { space } from 'helpers/style'
import { IconHash, IconLanguages, IconAudioTrack, IconRoom, IconWarning } from 'components/ui/icons'

import styled from 'styled'

import { stopSchema } from 'domains/org/getty/schemas/stop'
import { AttributeRef } from 'generated/mos/entity'
import { bffEndpoint } from 'helpers/bff'
import { Media } from 'generated/mos/media'

const Warning = styled.div`
  width: 20px;
`

const IconBlock = styled.div`
  display: flex;
  align-items: center;
  margin: 0 10px;
  font-weight: ${({ theme }) => theme.font.weight.base};
  color: ${({ theme }) => theme.color.grayL40};
`;

const IconText = styled.span<{ wide?: boolean }>`
  width: ${({ wide }) => wide ? space(45) : space(10)};
  margin-left: ${space(2)};
  font-size: ${({ theme }) => theme.font.size.small};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 0;
`

const DisplayBadge = styled.div`
  white-space: nowrap;
`;

const Badge = styled.div<{ red?: boolean }>`
  display: inline-block;
  margin-right: ${space(3)};
  padding: 1px 2px;
  border: 1px solid ${({ theme, red }) => red ? theme.color.error : theme.color.grayL20};
  border-radius: 2px;
  font-size: 10px;
  color: ${({ theme, red }) => red ? theme.color.error : theme.color.gray};
  font-weight: ${({ theme }) => theme.font.weight.light};
  text-transform: uppercase;
`

type Props = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly pointOfInterest: StopPointOfInterest.Entity;
  readonly deleteFromMood?: (pointOfInterestRef: StopPointOfInterest.Ref) => void;
  readonly moodId?: string;
  readonly placement?: string;
  readonly site?: string;
  readonly hasWarning?: boolean;
  readonly draggableIndex?: number;
  readonly isDragging?: boolean;
};

export const ListItem: React.FC<Props> = ({
  deleteFromMood,
  historyPush,
  projectId,
  pointOfInterest,
  moodId,
  placement,
  site,
  hasWarning,
  draggableIndex,
  isDragging,
}) => {
  const hostName = bffEndpoint(window.location.hostname)
  const pointOfInterestId = StopPointOfInterest.mustRef(pointOfInterest.ref).id
  const editStopPointOfInterestLocation = `/project/${projectId}/mood/${moodId}/point-of-interest/${pointOfInterestId}`
  const stopId = pointOfInterest.stop ? Stop.mustRef(pointOfInterest.stop.ref).id : ''
  const editStopLocation = `/project/${projectId}/stop/${stopId}`
  const stopImage = pointOfInterest && pointOfInterest.stop && stopSchema.image.read(pointOfInterest.stop.image) as Media.Ref
  const stopImageId = stopImage && stopImage.id
  const imagePath = stopImageId && hostName ? `${hostName}/images/${stopImageId}.jpg?s=crop&h=92&w=92` : ''

  let title: any = stopSchema.title.read(pointOfInterest.stop && pointOfInterest.stop.title)

  if (title && title.type === AttributeRef.refName) {
    title = ''
  }

  let producedBy: any = pointOfInterest && pointOfInterest.stop && stopSchema.producedBy.read(pointOfInterest.stop.producedBy)
  if (producedBy && producedBy.type === AttributeRef.refName) {
    producedBy = undefined
  }

  return(
    <Container isDragging={isDragging} onClick={() => historyPush(editStopPointOfInterestLocation)}>
      <Wrap isDragging={isDragging}>
        <Content>
          {draggableIndex !== undefined && (
            <>
              <DragHandle />
              <ItemIndex>
                {draggableIndex + 1}
              </ItemIndex>
            </>
          )}
          <Image src={imagePath} />
          <Description>
            <Title>{title}</Title>
            <DisplayBadge>
              {pointOfInterest.stop && pointOfInterest.stop.basedOnRef && <Badge red={!placement}>{placement ? "on display" : "not on display"}</Badge>}
              <Subtitle>{`${site ? `${site} - ` : ''}${producedBy ? producedBy : '-'}`}</Subtitle>
            </DisplayBadge>
          </Description>
        </Content>
        <IconsContainer>
          <IconBlock>
            <IconAudioTrack color="grayL40" size="small" />
            <IconText>{pointOfInterest.audio ? 'M4A' : '-'}</IconText>
          </IconBlock>
          <IconBlock>
            <IconRoom color="grayL40" size="small" />
            <IconText wide>{placement ? placement : '-'}</IconText>
          </IconBlock>
          <IconBlock>
            <Warning>{hasWarning && <IconWarning size="small" color="warning" />}</Warning>
          </IconBlock>
        </IconsContainer>
        <PopOverMenu iconSize="base">
          <PopOverMenuButton
            onClick={(event) => {
              historyPush(editStopPointOfInterestLocation)
              event.stopPropagation()
            }}
          >
            Edit Point of Interest
          </PopOverMenuButton>
          <PopOverMenuButton
            onClick={(event) => {
              historyPush(editStopLocation)
              event.stopPropagation()
            }}
          >
            Edit Stop
          </PopOverMenuButton>
          {deleteFromMood && (
            <PopOverMenuButton onClick={(event) => {
                deleteFromMood(StopPointOfInterest.mustRef(pointOfInterest.ref))
                event.stopPropagation()
              }
            }>
              Remove from Mood Journey
            </PopOverMenuButton>
          )}
        </PopOverMenu>
      </Wrap>
    </Container>
  )
}
